/**
 * pipeline-status — where a piece is in its pipeline.
 *
 * Extracted from the old WorkflowPanel (FEAT-087), which the side column
 * replaced. Kept as a pure, React-free module so the mapping can be tested:
 * it imports nothing that touches `import.meta.env`.
 */

import type { PieceDetail } from "@/types/piece";
import type { PipelinePhase } from "@shared/fiction-phases";

export type PhaseStatus = "done" | "running" | "pending" | "blocked" | "skipped";

export const PHASE_STATUS_LABEL: Record<PhaseStatus, string> = {
  done: "Done",
  running: "Running",
  pending: "Pending",
  blocked: "Blocked",
  skipped: "Skipped",
};

/**
 * The research phases, skipped together when a piece is "Only these sources".
 *
 * Mirrors RESEARCH_PHASES in apps/api/src/pipeline/skip-research.ts. They are
 * skipped as a pair because the Librarian cannot start without the Scout's
 * report — see that file for the full reasoning.
 */
const RESEARCH_PHASES = new Set(["phase_1_researched", "phase_2_outlined"]);

// Maps run_phase values (set at skill START by each skill) to the generation
// phase they are computing. This lets a stage show "Running" as soon as a
// skill begins, not just after it finishes.
const RUN_PHASE_TO_GEN_PHASE: Record<string, string> = {
  // Content pipeline
  initializing:              "phase_1_researched",
  "scout:searching":         "phase_1_researched",
  "scout:done":              "phase_1_researched",
  "librarian:fetching":      "phase_2_outlined",
  "librarian:writing_cards": "phase_2_outlined",
  "librarian:done":          "phase_2_outlined",
  "writer:drafting":         "phase_3_draft_generated",
  "writer:reporting":        "phase_3_draft_generated",
  "writer:done":             "phase_3_draft_generated",
  "auditor:checking":        "phase_4_fact_checked",
  "auditor:done":            "phase_4_fact_checked",
  "editor:correcting":       "phase_5_corrected",
  "editor:reporting":        "phase_5_corrected",
  "editor:done":             "phase_5_corrected",
  // FEAT-084 — the post-Editor voice check.
  "voice_evaluator:scoring": "phase_5b_voice_scored",
  "voice_evaluator:done":    "phase_5b_voice_scored",
  "seo:optimizing":          "phase_6_seo_optimized",
  "seo:done":                "phase_6_seo_optimized",
  "art-director:prompting":  "phase_7_art_directed",
  "art-director:done":       "phase_7_art_directed",
  "publisher:publishing":    "phase_8_published",
  // Fiction pipeline
  "fiction-worldbuilder:drafting":  "fiction_phase_1_worldbuilt",
  "fiction-worldbuilder:done":      "fiction_phase_1_worldbuilt",
  "fiction-architect:planning":     "fiction_phase_2_architected",
  "fiction-architect:done":         "fiction_phase_2_architected",
  "fiction-storyteller:drafting":   "fiction_phase_3_draft_generated",
  "fiction-storyteller:done":       "fiction_phase_3_draft_generated",
  "fiction-continuity:checking":    "fiction_phase_4_continuity_checked",
  "fiction-continuity:done":        "fiction_phase_4_continuity_checked",
  "fiction-prose-editor:polishing": "fiction_phase_5_corrected",
  "fiction-prose-editor:done":      "fiction_phase_5_corrected",
  "fiction-illustrator:generating": "fiction_phase_6_illustrated",
  "fiction-illustrator:done":       "fiction_phase_6_illustrated",
};

/**
 * Compute per-phase status from the piece's run_phase + generation_phases.
 *
 * - Phases up to and including `generation_phases` → "done".
 * - The phase mapped from `run_phase` (set at skill START) → "running".
 * - Rejected / failed / revision_requested → "blocked" for the next phase.
 * - Everything else → "pending".
 */
export function statusForPhase(
  phaseValue: string,
  phases: readonly PipelinePhase[],
  piece: PieceDetail
): PhaseStatus {
  // S-382 — a piece that does no web research. These two stages did not run
  // and never will, so they are neither pending (nothing is coming) nor done
  // (nothing was produced). Saying "Done" here is the defect d41bf1c fixed:
  // a stage claiming output it never generated. Checked before anything else,
  // because once the Writer sets phase_3_draft_generated the ordered-phase
  // rule below would otherwise report both as complete.
  if (piece.only_these_sources === true && RESEARCH_PHASES.has(phaseValue)) {
    return "skipped";
  }

  const completed = piece.generation_phases;
  const status = piece.status;
  const runPhase = piece.run_phase;
  const idx = phases.findIndex((p) => p.value === phaseValue);
  const completedIdx = completed
    ? phases.findIndex((p) => p.value === completed)
    : -1;

  if (idx <= completedIdx) return "done";

  // "queued" = job is in pg-boss queue but no skill is executing yet.
  // Treat it the same as null for phase-display: nothing is actively running.
  const isActive = runPhase != null && runPhase !== "done" && runPhase !== "error" && runPhase !== "queued";
  const activeGenPhase = isActive ? (RUN_PHASE_TO_GEN_PHASE[runPhase!] ?? null) : null;

  if (idx === completedIdx + 1) {
    if (status === "rejected" || status === "failed") return "blocked";
    if (status === "revision_requested") return "blocked";
    // Only "running" when a skill is actively executing (run_phase maps to a skill state).
    // "approved" with no active run → pending (not yet triggered, or queued but not started).
    if (isActive && (activeGenPhase === phaseValue || activeGenPhase == null)) return "running";
    return "pending";
  }

  // Edge case: run_phase maps to a phase further ahead than completedIdx+1
  if (isActive && activeGenPhase === phaseValue) return "running";

  return "pending";
}


/**
 * How many phases are complete — the "N of M" the side column shows.
 * A `generation_phases` value the phase list does not contain counts as 0
 * rather than as the whole pipeline.
 */
export function donePhaseCount(
  phases: readonly PipelinePhase[],
  currentPhase: string | null
): number {
  if (!currentPhase) return 0;
  const idx = phases.findIndex((p) => p.value === currentPhase);
  return idx === -1 ? 0 : idx + 1;
}

/**
 * The phase holding the piece: the first one not yet done. When every phase
 * is done the last one is returned, since nothing is waiting behind it.
 */
export function holdingPhase(
  phases: readonly PipelinePhase[],
  currentPhase: string | null
): PipelinePhase | null {
  if (phases.length === 0) return null;
  const done = donePhaseCount(phases, currentPhase);
  return phases[Math.min(done, phases.length - 1)] ?? null;
}
